import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { ArrowLeft, Clock, Plus, Loader2, Calendar } from "lucide-react";
import { obtenerTurnosZonaComun, TurnoZonaComun } from "../../../../api/zonaComunTurnosService";
import ManageTurnoModal from "./ManageTurnoModal";

export default function TurnosZonaComunPage() {
  const { codigo, diaCodigo } = useParams<{ codigo: string, diaCodigo: string }>();
  const location = useLocation();
  const zonaComunNombre = location.state?.zonaComunNombre || "";
  const diaNombre = location.state?.diaNombre || "Día Seleccionado";
  const horarioDia = location.state?.horarioDia || null;

  const navigate = useNavigate();
  const [turnos, setTurnos] = useState<TurnoZonaComun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedTurno, setSelectedTurno] = useState<TurnoZonaComun | null>(null);

  const loadTurnos = useCallback(async () => {
    if (!diaCodigo) return;
    
    try {
      setIsLoading(true);
      const response = await obtenerTurnosZonaComun({
        codigo: codigo || "",
        "zona comun horario por dia codigo": parseInt(diaCodigo),
        "estatus codigo": "A"
      });

      if (response.code === "1" && response.object_) {
        setTurnos(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar turnos:", error);
    } finally {
      setIsLoading(false);
    }
  }, [codigo, diaCodigo]);

  useEffect(() => {
    if (diaCodigo) {
      loadTurnos();
    }
  }, [diaCodigo, loadTurnos]);

  const handleEditTurno = (turno: TurnoZonaComun) => {
    setSelectedTurno(turno);
    setShowManageModal(true);
  };

  const handleCreateTurno = () => {
    setSelectedTurno(null);
    setShowManageModal(true);
  };

  const handleCloseModal = (saved: boolean) => {
    setShowManageModal(false);
    if (saved) {
      loadTurnos();
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all flex items-start justify-center">
        
        <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl overflow-hidden">
          {/* Header Verde */}
          <div className="bg-[#3d5a40] text-white p-4 flex items-center gap-3">
            <Calendar size={24} />
            <h2 className="text-xl font-bold">
              Turnos por Día - {diaNombre}
            </h2>
          </div>

          <div className="p-6">
            {/* Info Bar */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 flex flex-col sm:flex-row justify-between items-center text-gray-700 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2 sm:mb-0">
                <Calendar size={20} className="text-gray-500" />
                <span className="font-semibold text-lg">{diaNombre}</span>
              </div>
              
              {horarioDia && (
                <div className="flex items-center gap-4 text-sm font-medium">
                  <div className="flex items-center gap-1">
                    <Clock size={16} className="text-gray-400" />
                    <span>Apertura: <span className="font-bold">{horarioDia.zhd_hora_apertura}</span></span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={16} className="text-gray-400" />
                    <span>Cierre: <span className="font-bold">{horarioDia.zhd_hora_cierre}</span></span>
                  </div>
                </div>
              )}
            </div>

            {/* Grid de Turnos */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-10 w-10 animate-spin text-[#3d5a40]" />
              </div>
            ) : turnos.length === 0 ? (
              <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
                <Clock size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No hay turnos configurados para este día.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {turnos.map((turno) => {
                  const codigoTurno = turno.codigo || turno.ztr_codigo;
                  const inicio = turno["inicio turno"] || turno.ztr_inicio_turno;
                  const fin = turno["fin turno"] || turno.ztr_fin_turno;
                  const estatus = turno["estatus codigo"] || turno.ztr_estatus;
                  
                  return (
                    <div 
                      key={codigoTurno}
                      onClick={() => handleEditTurno(turno)}
                      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 p-5 cursor-pointer"
                    >
                      {/* Card Header */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-[#3d5a40] p-2.5 rounded-lg text-white">
                          <Clock size={20} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-800">
                            Turno {codigoTurno}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Código: {codigoTurno}
                          </p>
                        </div>
                      </div>

                      {/* Inicio Box (Azul) */}
                      <div className="bg-[#E6F0FF] rounded-lg p-3 mb-3 flex items-center gap-3">
                        <div className="bg-[#2563EB] p-1.5 rounded-full text-white">
                          <Clock size={16} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Inicio</span>
                          <span className="text-lg font-bold text-[#2563EB]">{inicio}</span>
                        </div>
                      </div>

                      {/* Fin Box (Morado/Rosa) */}
                      <div className="bg-[#FDF2F8] rounded-lg p-3 mb-4 flex items-center gap-3">
                        <div className="bg-[#DB2777] p-1.5 rounded-full text-white">
                          <Clock size={16} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Fin</span>
                          <span className="text-lg font-bold text-[#DB2777]">{fin}</span>
                        </div>
                      </div>

                      {/* Footer Badge */}
                      <div>
                        <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold border ${
                          estatus === 'A' 
                            ? 'bg-green-50 text-green-600 border-green-200' 
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}>
                          {estatus === 'A' ? 'ACTIVO' : 'INACTIVO'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer Buttons */}
            <div className="mt-8 pt-4 border-t border-gray-100 flex justify-between items-center">
              <Button
                onClick={handleCreateTurno}
                className="bg-[#3d5a40] hover:bg-[#2f4632] text-white font-semibold px-6"
              >
                <Plus size={18} className="mr-2" />
                Agregar Turno por Día
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-gray-200 text-gray-700 hover:bg-gray-50 px-6"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>

        <ManageTurnoModal
          isOpen={showManageModal}
          diaCodigo={parseInt(diaCodigo || "0")}
          turnoToEdit={selectedTurno}
          onClose={handleCloseModal}
        />
      </main>
    </div>
  );
}
